import { inject, Injectable, model } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { delay, finalize, map, switchMap } from 'rxjs/operators';
import { LoadingService } from '../loading.service';
import { MaintenanceService } from '../maintenance.service';
import {
  PartId,
  PartSubType,
  PartType,
  PartWithMaintenanceDetails,
} from 'src/app/models/maintenancelogs.model';
import { UAV_HR_TO_NAME } from 'src/app/models/uav';
import { EquipmentService } from 'src/app/state-services/equipment.service';
import { AircraftDetails } from 'src/app/models/equipment.model';

export interface MaintenanceLogFormMetaData {
  aircraftName: string;
  unitNumber: string;
  partList: PartId[];
  maintenanceType: string[];
  maintenanceLog?: PartWithMaintenanceDetails;
}

@Injectable({
  providedIn: 'root',
})
export class MaintenanceLogFormResolverService implements Resolve<any> {
  private loadingService = inject(LoadingService);
  private maintenanceService = inject(MaintenanceService);
  private equipmentService = inject(EquipmentService);
  sample: MaintenanceLogFormMetaData = {
    aircraftName: 'Vector 34',
    unitNumber: '12345',
    partList: [
      {
        id: '101',
        partType: PartType.BODY,
        partSubType: PartSubType.VECOTORR24,
      },
      {
        id: '102',
        partType: PartType.LEFT_WING,
        partSubType: PartSubType.VECOTORR25,
      },
    ],
    maintenanceType: ['Preventive', 'Corrective'],
  };

  constructor() {}
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<MaintenanceLogFormMetaData> {
    this.loadingService.loading();
    const modelKey = route.paramMap.get('modelKey');
    const unitNumber = route.paramMap.get('unitNumber');
    const logId = route.paramMap.get('logId');
    const configType = route.paramMap.get('configType');
    (this.sample.aircraftName = UAV_HR_TO_NAME[+modelKey!]),
      (this.sample.unitNumber = unitNumber!);

    if (!modelKey || !unitNumber) {
      return of(this.sample); // Default value if no parameters are found
    }
    // Simulate an API call to fetch the car name based on modelKey and unitNumber
    return this.getLog(modelKey, unitNumber, configType, logId);
  }

  private getEquipmentDetails(
    modelKey: string,
    unitNumber: string,
  ): Observable<MaintenanceLogFormMetaData> {
    // Simulating an async operation (API call)
    return this.equipmentService
      .getDroneDetails(modelKey, unitNumber, true)
      .pipe(
        map((details: AircraftDetails) => ({
          aircraftName: details.model.name,
          unitNumber: UAV_HR_TO_NAME[details.model.key],
          partList: details.parts ?? [],
          maintenanceType: ['Preventive', 'Corrective'],
        })),
        finalize(() => this.loadingService.loaded()),
      );
  }

  private getLog(
    modelKey: string,
    unitNumber: string,
    configType?: string | null,
    logId?: string | null,
  ): Observable<MaintenanceLogFormMetaData> {
    if (!logId) {
      return this.getEquipmentDetails(modelKey, unitNumber);
    }

    return this.getEquipmentDetails(modelKey, unitNumber).pipe(
      switchMap((baseMetaData) =>
        this.maintenanceService
          .getMaintenanceLogsFromConfigurationsOfLatestFlightOfAircraft(
            modelKey,
            unitNumber,
            configType!,
          )
          .pipe(
            map((logs: PartWithMaintenanceDetails[]) => {
              const filteredLogs = logs.find((el) => el.id === logId);
              return {
                ...baseMetaData,
                maintenanceLog: filteredLogs ?? undefined,
              };
            }),
            finalize(() => this.loadingService.loaded()),
          ),
      ),
    );
  }
}
